import { logger } from '../../utils/logger.js'
import maplibregl from 'maplibre-gl'
import { Capacitor } from '@capacitor/core'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { cotrexLayerService } from './cotrex-layer-service.js'
import type { SelectableLayerProvider } from '../selection/highlight-adapter.js'
import type { SelectableFeatureRef } from '../selection/selectable-feature-ref.js'

const log = logger.create('cotrex-closures')

const TILE_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const PMTILES_URL = `${TILE_ORIGIN}/data/cotrex/cotrex.pmtiles`

const SOURCE_ID = 'cotrex-source'
const CLOSURE_FILL_LAYER = 'cotrex-closures-fill'
const CLOSURE_LINE_LAYER = 'cotrex-closures-outline'
const CLOSURE_LABEL_LAYER = 'cotrex-closures-label'

const DEFAULT_OPACITY = 0.8
const CLOSURE_COLOR = '#ef4444'
const CLOSURE_OUTLINE_COLOR = '#dc2626'

function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

function activeDateFilter(): maplibregl.ExpressionSpecification {
  const today = todayISO()
  return ['all', ['<=', ['get', 'start_date'], today], ['>=', ['get', 'end_date'], today]]
}

class CotrexClosureService implements SelectableLayerProvider {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY

  get enabled(): boolean {
    return this._enabled
  }

  get opacity(): number {
    return this._opacity
  }

  register(map: maplibregl.Map): void {
    this.map = map
  }

  unregister(): void {
    this.disable()
    this.map = null
  }

  private ensureSource(): void {
    if (!this.map || this.map.getSource(SOURCE_ID)) return
    this.map.addSource(SOURCE_ID, {
      type: 'vector',
      url: `pmtiles://${PMTILES_URL}`,
      minzoom: 6,
      maxzoom: 14,
    })
  }

  enable(): void {
    if (!this.map || this._enabled) return
    this._enabled = true
    this.ensureSource()

    const filter = activeDateFilter()

    if (!this.map.getLayer(CLOSURE_FILL_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: CLOSURE_FILL_LAYER,
          type: 'fill',
          source: SOURCE_ID,
          'source-layer': 'cotrex_closures',
          minzoom: 6,
          filter,
          paint: {
            'fill-color': CLOSURE_COLOR,
            'fill-opacity': 0.2 * this._opacity,
          },
        },
        'cotrex-closures'
      )
    }

    if (!this.map.getLayer(CLOSURE_LINE_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: CLOSURE_LINE_LAYER,
          type: 'line',
          source: SOURCE_ID,
          'source-layer': 'cotrex_closures',
          minzoom: 6,
          filter,
          paint: {
            'line-color': CLOSURE_OUTLINE_COLOR,
            'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.5, 10, 1.5, 14, 2.5],
            'line-opacity': this._opacity,
          },
        },
        'cotrex-closures'
      )
    }

    if (!this.map.getLayer(CLOSURE_LABEL_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: CLOSURE_LABEL_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'cotrex_closures',
          minzoom: 11,
          filter,
          layout: {
            'text-field': ['get', 'name'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 11, 10, 14, 13],
            'text-font': ['Open Sans Regular'],
            'text-anchor': 'center',
            'text-max-width': 12,
          },
          paint: {
            'text-color': CLOSURE_OUTLINE_COLOR,
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5,
            'text-opacity': this._opacity,
          },
        },
        'cotrex-closures'
      )
    }

    log.info('Closures enabled')
  }

  disable(): void {
    if (!this.map) return
    this._enabled = false

    if (this.map.getLayer(CLOSURE_LABEL_LAYER)) this.map.removeLayer(CLOSURE_LABEL_LAYER)
    if (this.map.getLayer(CLOSURE_LINE_LAYER)) this.map.removeLayer(CLOSURE_LINE_LAYER)
    if (this.map.getLayer(CLOSURE_FILL_LAYER)) this.map.removeLayer(CLOSURE_FILL_LAYER)

    if (this.map.getSource(SOURCE_ID) && !cotrexLayerService.enabled) {
      this.map.removeSource(SOURCE_ID)
    }
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  reAdd(): void {
    if (!this.map || !this._enabled) return
    if (!this.map.getSource(SOURCE_ID)) {
      this.enable()
    }
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (this.map?.getLayer(CLOSURE_FILL_LAYER)) {
      this.map.setPaintProperty(CLOSURE_FILL_LAYER, 'fill-opacity', 0.2 * this._opacity)
    }
    if (this.map?.getLayer(CLOSURE_LINE_LAYER)) {
      this.map.setPaintProperty(CLOSURE_LINE_LAYER, 'line-opacity', this._opacity)
    }
    if (this.map?.getLayer(CLOSURE_LABEL_LAYER)) {
      this.map.setPaintProperty(CLOSURE_LABEL_LAYER, 'text-opacity', this._opacity)
    }
  }

  getSelectableLayerIds(): string[] {
    if (!this._enabled) return []
    const layers: string[] = []
    if (this.map?.getLayer(CLOSURE_FILL_LAYER)) layers.push(CLOSURE_FILL_LAYER)
    return layers
  }

  /** Layer ids that respond to identify (for the hover cursor). */
  interactiveLayerIds(): string[] {
    return [CLOSURE_FILL_LAYER, CLOSURE_LINE_LAYER]
  }

  featureToRef(hit: maplibregl.MapGeoJSONFeature): SelectableFeatureRef | null {
    if (hit.layer.id !== CLOSURE_FILL_LAYER && hit.layer.id !== CLOSURE_LINE_LAYER) return null
    const name = (hit.properties.name as string | undefined) ?? ''
    if (!name) return null
    return { source: 'cotrex-closure', name }
  }

  queryFeatures(point: maplibregl.Point): maplibregl.MapGeoJSONFeature[] {
    if (!this.map || !this._enabled) return []
    const layers: string[] = []
    if (this.map.getLayer(CLOSURE_FILL_LAYER)) layers.push(CLOSURE_FILL_LAYER)
    if (layers.length === 0) return []
    return this.map.queryRenderedFeatures(point, { layers })
  }
}

export const cotrexClosureService = new CotrexClosureService()
